import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus } from 'lucide-react';
import { formatCurrency } from './helpers';

export const ChangeOrdersTab = ({ changeOrders, approvedCOAmount, pendingCOAmount }) => {
  const navigate = useNavigate();

  return (
    <Card className='border-slate-200 shadow-sm rounded-sm'>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <CardTitle className='text-xl font-bold text-slate-800'>
            Change Orders
            <span className='ml-2 text-lg font-normal text-slate-500'>({changeOrders.length} total)</span>
          </CardTitle>
          <Button onClick={() => navigate('/dashboard/change-orders')} variant='outline' size='sm'
            className='rounded-sm border-2 border-[#1A3668] text-[#1A3668] hover:bg-[#1A3668] hover:text-white'>
            <Plus className='w-4 h-4 mr-2' />New Change Order
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-slate-50 rounded-sm'>
          <div>
            <p className='text-xs font-bold uppercase text-slate-500'>Approved</p>
            <p className='text-xl font-bold text-[#16A34A]'>{formatCurrency(approvedCOAmount)}</p>
            <p className='text-xs text-slate-500'>{changeOrders.filter(co => co.status === 'APPROVED').length} COs</p>
          </div>
          <div>
            <p className='text-xs font-bold uppercase text-slate-500'>Pending</p>
            <p className='text-xl font-bold text-amber-600'>{formatCurrency(pendingCOAmount)}</p>
            <p className='text-xs text-slate-500'>{changeOrders.filter(co => co.status === 'PENDING').length} COs</p>
          </div>
          <div>
            <p className='text-xs font-bold uppercase text-slate-500'>Draft</p>
            <p className='text-xl font-bold text-slate-600'>
              {formatCurrency(changeOrders.filter(co => co.status === 'DRAFT').reduce((sum, co) => sum + (co.costImpact || 0), 0))}
            </p>
            <p className='text-xs text-slate-500'>{changeOrders.filter(co => co.status === 'DRAFT').length} COs</p>
          </div>
          <div>
            <p className='text-xs font-bold uppercase text-slate-500'>Additional Scope</p>
            <p className='text-xl font-bold text-blue-600'>{formatCurrency(approvedCOAmount)}</p>
            <p className='text-xs text-slate-500'>Added to PO</p>
          </div>
        </div>
        <div className='overflow-x-auto'>
          <table className='w-full border-collapse text-sm'>
            <thead>
              <tr className='bg-slate-100 border-b-2 border-slate-200'>
                <th className='text-left p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>CO #</th>
                <th className='text-left p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Title</th>
                <th className='text-right p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Cost Impact</th>
                <th className='text-center p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Status</th>
                <th className='text-left p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Date</th>
              </tr>
            </thead>
            <tbody>
              {changeOrders.map((co) => (
                <tr key={co.id} className='border-b border-slate-200 hover:bg-slate-50'>
                  <td className='p-2 font-mono font-medium text-slate-700'>CO-{String(co.coNumber).padStart(3, '0')}</td>
                  <td className='p-2 text-slate-700'>{co.title}</td>
                  <td className={`p-2 text-right font-mono font-bold ${co.costImpact >= 0 ? 'text-[#16A34A]' : 'text-red-600'}`}>
                    {co.costImpact >= 0 ? '+' : ''}{formatCurrency(co.costImpact)}
                  </td>
                  <td className='p-2 text-center'>
                    <Badge className={`rounded-sm text-xs ${
                      co.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                      co.status === 'PENDING' ? 'bg-amber-100 text-amber-800' :
                      co.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                      'bg-slate-100 text-slate-800'
                    }`}>{co.status}</Badge>
                  </td>
                  <td className='p-2 font-mono text-slate-600'>
                    {co.approvedAt ? co.approvedAt.split('T')[0] : co.createdAt?.split('T')[0]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {changeOrders.length === 0 && (
            <div className='text-center p-8 text-slate-500'>No change orders for this job</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
